import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  usePharmacyDrug,
  useCreatePharmacyDrug,
  useUpdatePharmacyDrug,
  usePharmacyDrugCategories,
  usePharmacyGenericNames,
  usePharmacyCompanies,
  usePharmacyStorageRacks,
  useSetPharmacyStock,
} from '../../services/httpServices/queries';
import type { DrugForm, PharmacyItemType } from '../../services/httpServices/pharmacyDrugService';
import { PharmacyLayout } from './PharmacyLayout';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Plus,
  Save,
  X,
  Pill,
  Tags,
  FlaskConical,
  Building2,
  LayoutGrid,
  Package,
  DollarSign,
} from 'lucide-react';

const DRUG_FORMS = [
  'TABLET',
  'CAPSULE',
  'SYRUP',
  'SUSPENSION',
  'INJECTION',
  'OINTMENT',
  'CREAM',
  'DROPS',
  'INHALER',
  'POWDER',
  'OTHER',
];

/** Human-friendly labels for API field names, used to translate validation errors. */
const FIELD_LABELS: Record<string, string> = {
  storageRackId: 'storage rack',
  categoryId: 'category',
  genericNameId: 'generic name',
  companyId: 'company',
  drugCode: 'drug code',
  name: 'drug name',
  unitOfMeasure: 'unit of measure',
  price: 'price',
};

/**
 * Turn raw validation messages such as "storageRackId must be a UUID" into
 * messages a non-technical pharmacy user can understand and act on.
 */
function toFriendlyError(raw: unknown): string {
  const text = Array.isArray(raw) ? raw.join(', ') : String(raw ?? '');
  if (!text.trim()) return 'Something went wrong. Please try again.';

  return text
    .split(', ')
    .map((part) => {
      const match = part.match(/^([A-Za-z0-9_]+) must be a UUID$/i);
      if (match) {
        const label = FIELD_LABELS[match[1]] || 'selected value';
        return `Please select a valid ${label}.`;
      }
      return part;
    })
    .join(' ');
}

export function PharmacyDrugForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const { data: drugData, isLoading: drugLoading, error: drugError } = usePharmacyDrug(id);
  const { data: categoriesData, isLoading: categoriesLoading } = usePharmacyDrugCategories(1, 1000);
  const { data: genericsData, isLoading: genericsLoading } = usePharmacyGenericNames(1, 1000);
  const { data: companiesData, isLoading: companiesLoading } = usePharmacyCompanies(1, 1000);
  const { data: racksData, isLoading: racksLoading } = usePharmacyStorageRacks();

  const createMutation = useCreatePharmacyDrug();
  const updateMutation = useUpdatePharmacyDrug();
  const setStockMutation = useSetPharmacyStock();

  const [formData, setFormData] = useState({
    drugCode: '',
    name: '',
    form: 'TABLET' as DrugForm,
    itemType: 'MEDICINE' as PharmacyItemType,
    strength: '',
    categoryId: '',
    genericNameId: '',
    companyId: '',
    storageRackId: '',
    unitOfMeasure: '',
    price: 0,
    reorderLevel: 10,
    reorderQuantity: 50,
    stock: 0,
    description: '',
    isActive: true,
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);

  const drug = drugData?.data;
  const categories = categoriesData?.data || [];
  const generics = genericsData?.data || [];
  const companies = companiesData?.data || [];
  const racks = racksData?.data || [];

  // Auto-generate drug code from name
  useEffect(() => {
    if (isEdit) return;
    if (!formData.name.trim()) return;
    if (codeManuallyEdited) return;
    const generated = `MDC-${formData.name.replace(/[^a-zA-Z0-9]/g, '').slice(0, 4).toUpperCase()}`;
    setFormData((prev) => ({ ...prev, drugCode: generated }));
  }, [formData.name, codeManuallyEdited, isEdit]);

  useEffect(() => {
    if (isEdit && drug) {
      setFormData({
        drugCode: drug.drugCode || '',
        name: drug.name || '',
        form: drug.form || 'TABLET',
        itemType: drug.itemType || 'MEDICINE',
        strength: drug.strength || '',
        categoryId: drug.categoryId || '',
        genericNameId: drug.genericNameId || '',
        companyId: drug.companyId || '',
        storageRackId: drug.storageRackId || '',
        unitOfMeasure: drug.unitOfMeasure || '',
        price: Number(drug.price) || 0,
        reorderLevel: Number(drug.reorderLevel) || 0,
        reorderQuantity: Number(drug.reorderQuantity) || 0,
        stock: Number(drug.currentStock) || 0,
        description: drug.description || '',
        isActive: drug.isActive ?? true,
      });
      setCodeManuallyEdited(true);
    }
  }, [isEdit, drug]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.drugCode.trim()) errors.drugCode = 'Drug code is required';
    if (!formData.name.trim()) errors.name = 'Drug name is required';
    if (!formData.categoryId) errors.categoryId = 'Category is required';
    if (!formData.genericNameId) errors.genericNameId = 'Generic name is required';
    if (!formData.companyId) errors.companyId = 'Company is required';
    if (!formData.unitOfMeasure.trim()) errors.unitOfMeasure = 'Unit of measure is required';
    if (formData.price < 0) errors.price = 'Price cannot be negative';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Optional UUID fields must be omitted or null — never an empty string,
    // otherwise `@IsUUID()` rejects `''` even though the field is optional.
    const payload: Record<string, any> = { ...formData };
    // `stock` is not a drug column; it is applied through the stock API below.
    delete payload.stock;
    if (!payload.storageRackId) {
      payload.storageRackId = isEdit ? null : undefined;
    }

    const targetStock = Math.max(0, Number(formData.stock) || 0);

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ id: id!, data: payload });
        // Always reconcile: this also repairs drugs whose stock was previously
        // recorded in the ledger without a matching batch.
        await setStockMutation.mutateAsync({
          drugId: id!,
          quantity: targetStock,
          reason: 'Updated from drug form',
        });
      } else {
        const created = await createMutation.mutateAsync(payload as any);
        const createdId = created?.data?.id;
        if (createdId && targetStock > 0) {
          await setStockMutation.mutateAsync({
            drugId: createdId,
            quantity: targetStock,
            reason: 'Opening stock',
          });
        }
      }
      navigate('/pharmacy/drugs');
    } catch (err: any) {
      setFormErrors({ general: toFriendlyError(err?.response?.data?.message ?? err?.message) });
    }
  };

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'drugCode') setCodeManuallyEdited(true);
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const inputBase = 'w-full px-3 py-2 border rounded-lg bg-background text-text focus:outline-none focus:ring-2 text-sm transition-all';
  const inputValid = 'border-border focus:ring-primary';
  const inputInvalid = 'border-danger focus:ring-danger';
  const labelClass = 'block text-sm font-medium text-text mb-1';
  const ic = (field: string) => `${inputBase} ${formErrors[field] ? inputInvalid : inputValid}`;
  const err = (field: string) => formErrors[field] ? <p className="text-xs text-danger mt-1">{formErrors[field]}</p> : null;

  const isLoading = drugLoading || categoriesLoading || genericsLoading || companiesLoading || racksLoading;

  if (isEdit && isLoading) {
    return (
      <PharmacyLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </PharmacyLayout>
    );
  }

  if (isEdit && (drugError || !drug)) {
    return (
      <PharmacyLayout>
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load drug details. Please try again.</span>
        </div>
      </PharmacyLayout>
    );
  }

  return (
    <PharmacyLayout title={isEdit ? 'Edit Drug' : 'Add New Drug'} description={isEdit ? 'Update medicine details' : 'Create a new medicine entry'}>
      <button
        onClick={() => navigate('/pharmacy/drugs')}
        className="inline-flex items-center gap-2 text-text-muted hover:text-text transition-colors text-sm cursor-pointer"
      >
        <ArrowLeft size={18} />
        Back to Drug List
      </button>

      {formErrors.general && (
        <div className="bg-danger/10 text-danger rounded-lg p-4 flex items-center gap-2">
          <AlertCircle size={18} />
          <span className="text-sm font-medium">{formErrors.general}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-surface border border-border rounded-xl p-6 space-y-6 shadow-sm">
        {/* Basic Information */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-text font-medium border-b border-border pb-2">
            <Pill size={18} className="text-primary" />
            <span>Basic Information</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>
                Drug Code * {!isEdit && <span className="text-text-muted font-normal">(auto)</span>}
              </label>
              <input
                type="text"
                className={`${ic('drugCode')} font-mono`}
                value={formData.drugCode}
                onChange={(e) => updateField('drugCode', e.target.value)}
                placeholder="MDC-NAPA"
              />
              {err('drugCode')}
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Drug Name *</label>
              <input
                type="text"
                className={ic('name')}
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="Napa 500mg Tablet"
              />
              {err('name')}
            </div>
            <div>
              <label className={labelClass}>Item Type</label>
              <select
                className={`${inputBase} ${inputValid}`}
                value={formData.itemType}
                onChange={(e) => updateField('itemType', e.target.value)}
              >
                <option value="MEDICINE">Medicine</option>
                <option value="ACCESSORY">Accessory / Consumable</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Form</label>
              <select
                className={`${inputBase} ${inputValid}`}
                value={formData.form}
                onChange={(e) => updateField('form', e.target.value)}
              >
                {DRUG_FORMS.map((form) => (
                  <option key={form} value={form}>
                    {form}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Strength</label>
              <input
                type="text"
                className={`${inputBase} ${inputValid}`}
                value={formData.strength}
                onChange={(e) => updateField('strength', e.target.value)}
                placeholder="500mg"
              />
            </div>
            <div>
              <label className={labelClass}>Unit of Measure *</label>
              <input
                type="text"
                className={ic('unitOfMeasure')}
                value={formData.unitOfMeasure}
                onChange={(e) => updateField('unitOfMeasure', e.target.value)}
                placeholder="tablet"
              />
              {err('unitOfMeasure')}
            </div>
          </div>
        </div>

        {/* Relations */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-text font-medium border-b border-border pb-2">
            <Tags size={18} className="text-primary" />
            <span>Relations</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Category *</label>
              <div className="flex gap-2">
                <select
                  className={`${ic('categoryId')} flex-1`}
                  value={formData.categoryId}
                  onChange={(e) => updateField('categoryId', e.target.value)}
                >
                  <option value="">Select category</option>
                  {categories.map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => navigate('/pharmacy/categories')}
                  className="px-3 py-2 bg-background border border-border rounded-lg text-text-muted hover:text-primary transition-colors"
                  title="Manage Categories"
                >
                  <Tags size={16} />
                </button>
              </div>
              {err('categoryId')}
            </div>
            <div>
              <label className={labelClass}>Generic Name *</label>
              <div className="flex gap-2">
                <select
                  className={`${ic('genericNameId')} flex-1`}
                  value={formData.genericNameId}
                  onChange={(e) => updateField('genericNameId', e.target.value)}
                >
                  <option value="">Select generic name</option>
                  {generics.map((g: any) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => navigate('/pharmacy/generics')}
                  className="px-3 py-2 bg-background border border-border rounded-lg text-text-muted hover:text-primary transition-colors"
                  title="Manage Generic Names"
                >
                  <FlaskConical size={16} />
                </button>
              </div>
              {err('genericNameId')}
            </div>
            <div>
              <label className={labelClass}>Company *</label>
              <div className="flex gap-2">
                <select
                  className={`${ic('companyId')} flex-1`}
                  value={formData.companyId}
                  onChange={(e) => updateField('companyId', e.target.value)}
                >
                  <option value="">Select company</option>
                  {companies.map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => navigate('/pharmacy/companies')}
                  className="px-3 py-2 bg-background border border-border rounded-lg text-text-muted hover:text-primary transition-colors"
                  title="Manage Companies"
                >
                  <Building2 size={16} />
                </button>
              </div>
              {err('companyId')}
            </div>
            <div>
              <label className={labelClass}>Storage Rack</label>
              <div className="flex gap-2">
                <select
                  className={`${inputBase} ${inputValid} flex-1`}
                  value={formData.storageRackId}
                  onChange={(e) => updateField('storageRackId', e.target.value)}
                >
                  <option value="">Select storage rack</option>
                  {racks.map((r: any) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => navigate('/pharmacy/racks')}
                  className="px-3 py-2 bg-background border border-border rounded-lg text-text-muted hover:text-primary transition-colors"
                  title="Manage Storage Racks"
                >
                  <LayoutGrid size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing & Stock */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-text font-medium border-b border-border pb-2">
            <DollarSign size={18} className="text-primary" />
            <span>Pricing & Stock</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className={labelClass}>Current Stock</label>
              <input
                type="number"
                min="0"
                className={`${inputBase} ${inputValid}`}
                value={formData.stock}
                onChange={(e) => updateField('stock', Math.max(0, Number(e.target.value)))}
              />
              <p className="text-xs text-text-muted mt-1">
                On-hand quantity{formData.unitOfMeasure ? ` (${formData.unitOfMeasure})` : ''}
              </p>
            </div>
            <div>
              <label className={labelClass}>Price (৳)</label>
              <ValidatedNumberInput
                className={`${inputBase} ${inputValid}`}
                value={formData.price}
                onChange={(v) => updateField('price', v)}
              />
            </div>
            <div>
              <label className={labelClass}>Reorder Level</label>
              <input
                type="number"
                min="0"
                className={`${inputBase} ${inputValid}`}
                value={formData.reorderLevel}
                onChange={(e) => updateField('reorderLevel', Number(e.target.value))}
              />
            </div>
            <div>
              <label className={labelClass}>Reorder Quantity</label>
              <input
                type="number"
                min="0"
                className={`${inputBase} ${inputValid}`}
                value={formData.reorderQuantity}
                onChange={(e) => updateField('reorderQuantity', Number(e.target.value))}
              />
            </div>
          </div>
        </div>

        {/* Description & Status */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-text font-medium border-b border-border pb-2">
            <Package size={18} className="text-primary" />
            <span>Additional Details</span>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className={labelClass}>Description</label>
              <textarea
                rows={3}
                className={`${inputBase} ${inputValid}`}
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Optional description or notes about the drug"
              />
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => updateField('isActive', e.target.checked)}
                className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
              />
              <label htmlFor="isActive" className="text-sm text-text cursor-pointer">
                Active
              </label>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-4 border-t border-border">
          <button
            type="button"
            onClick={() => navigate('/pharmacy/drugs')}
            className="px-4 py-2 text-text-muted hover:text-text transition-colors text-sm flex items-center gap-2"
          >
            <X size={16} />
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending || setStockMutation.isPending}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2 shadow-sm"
          >
            {(createMutation.isPending || updateMutation.isPending || setStockMutation.isPending) && <Loader2 size={16} className="animate-spin" />}
            {isEdit ? <Save size={16} /> : <Plus size={16} />}
            {isEdit ? 'Save Changes' : 'Add Drug'}
          </button>
        </div>
      </form>
    </PharmacyLayout>
  );
}
